import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { cardinalApi, guard } from '../services/auth.api'
import {
  AuthenticationMethod,
  CardinalBaseSdk,
  CardinalSdk,
  DecryptedDocument,
  DelegationTag,
  DocumentApi,
  HealthcareParty,
  User,
} from '@icure/cardinal-sdk'
import { ICURE_CLOUD_URL } from '../../constants'
import { PatientFilters } from '@icure/cardinal-sdk/filters.mjs'
import { findProperty, findPropertyValue, isLegacyProperty, withUpsertedProperty } from '../../helpers/property'

import { v4 as uuid } from 'uuid'

export const MONA_KEY_INDENTIFIER = 'MONA_API_KEY_DOCUMENT_ID'
export const MEDISPRING_INDENTIFIER = 'MEDISPRING_DOCUMENT_ID'

export const startReplications = (sdk: CardinalSdk) => {
  setInterval(async () => {
    const currentUser = await sdk.user.getCurrentUser()
    const currentHcp = await sdk.healthcareParty.getHealthcareParty(currentUser.healthcarePartyId!).catch(() => null)
    const existingMedispringDocumentId: string | undefined = findPropertyValue(currentHcp?.properties, MEDISPRING_INDENTIFIER)

    if (existingMedispringDocumentId) {
      const existingMedispringDocument = await sdk.document.getDocument(existingMedispringDocumentId).catch(() => null)

      if (!existingMedispringDocument) {
        throw new Error('MedispringDocument document is missing')
      }

      const mainAttachment = await sdk.document.getAndDecryptMainAttachment(existingMedispringDocument).catch(() => null)
      if (mainAttachment) {
        try {
          const { username, password, dataOwner } = JSON.parse(new TextDecoder().decode(mainAttachment)) as {
            username: string
            password: string
            dataOwner: string
          }
          const otherSdk = await CardinalBaseSdk.initialize(undefined, ICURE_CLOUD_URL, new AuthenticationMethod.UsingCredentials.UsernamePassword(username, password))

          //Fetch by using the token obtained from the sdk
          const fromToken = await sdk.auth.getBearerToken()
          const toToken = await otherSdk.auth.getBearerToken()

          const exchangeData = await fetch(`${ICURE_CLOUD_URL}/rest/v2/exchangedata/byParticipant/${dataOwner}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${fromToken}`,
            },
          }).then((res) => res.json())

          const existingExchangeDataOnDestination = await fetch(`${ICURE_CLOUD_URL}/rest/v2/exchangedata/byParticipant/${dataOwner}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${toToken}`,
            },
          }).then((res) => res.json())

          await Promise.all(
            exchangeData.rows.map(async (ed: any) => {
              if (!existingExchangeDataOnDestination.rows.find((eed: any) => eed.id === ed.id)) {
                delete ed.rev
                await fetch(`${ICURE_CLOUD_URL}/rest/v2/exchangedata`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${toToken}`,
                  },
                  body: JSON.stringify(ed),
                })
              }
            }),
          )

          const patients = await sdk.patient.encrypted.filterPatientsBy(PatientFilters.allPatientsForSelf())
          while (await patients.hasNext()) {
            const patientsBatch = await patients.next(10)
            await Promise.all(
              patientsBatch.map(async (p) => {
                const existingPatientOnDestination = await otherSdk.patient.getPatient(p.id).catch(() => null)
                if (!existingPatientOnDestination) {
                  delete p.rev
                  await otherSdk.patient.createPatient(p)
                }
              }),
            )
          }
        } catch (e) {
          console.error('Failed to replicate from Medispring instance', e)
        }
      }
    }
  }, 60000)
}

export const interopApiRtk = createApi({
  reducerPath: 'interopApi',
  tagTypes: ['Crypto', 'Practitioner'],
  baseQuery: fetchBaseQuery({
    baseUrl: '',
  }),
  endpoints: (builder) => ({
    giveAccessToNewHealthcarePartyWithPrivateKey: builder.mutation<
      boolean | undefined,
      {
        username: string
        password: string
      }
    >({
      async queryFn({ username, password }, { getState }) {
        const { healthcareParty: healthcarePartyApi, user: userApi, document: documentApi } = (await cardinalApi(getState)) ?? {}
        return guard([healthcarePartyApi, userApi, documentApi], async ([healthcarePartyApi, userApi, documentApi]): Promise<boolean> => {
          const currentUser = await userApi.getCurrentUser()
          if (!currentUser) {
            throw new Error('You are not logged in')
          }
          const selfOnRB = await healthcarePartyApi.getHealthcareParty(currentUser.healthcarePartyId!)
          let selfOnMs: HealthcareParty | undefined
          let parentOfSelfOnMS: HealthcareParty | undefined

          try {
            const otherSdk = await CardinalBaseSdk.initialize(undefined, ICURE_CLOUD_URL, new AuthenticationMethod.UsingCredentials.UsernamePassword(username, password))
            selfOnMs = await otherSdk.healthcareParty.getCurrentHealthcareParty()

            parentOfSelfOnMS = selfOnMs?.parentId ? await otherSdk.healthcareParty.getHealthcareParty(selfOnMs.parentId).catch(() => undefined) : undefined
          } catch (e) {
            throw new Error('Failed to authenticate with Medispring instance. Please check your credentials.')
          }

          delete selfOnMs.rev
          delete parentOfSelfOnMS?.rev

          // RB stands for RetinoBridge
          let selfOrParentOnRB: HealthcareParty | undefined

          if (parentOfSelfOnMS === undefined) {
            let medispringHcpOnRB = await healthcarePartyApi.getHealthcareParty(selfOnMs.id).catch(() => null)
            if (!medispringHcpOnRB) {
              medispringHcpOnRB = await healthcarePartyApi.createHealthcareParty(selfOnMs)
            }

            if (!medispringHcpOnRB) {
              throw new Error('Failed to create or retrieve healthcare party')
            }

            selfOrParentOnRB = medispringHcpOnRB
          } else {
            let parentHere = await healthcarePartyApi.getHealthcareParty(parentOfSelfOnMS.id).catch(() => null)
            if (!parentHere) {
              parentHere = await healthcarePartyApi.createHealthcareParty(parentOfSelfOnMS)
            }

            if (!parentHere) {
              throw new Error('Failed to create or retrieve healthcare party')
            }

            selfOrParentOnRB = parentHere
          }

          const existingMedispringDocumentId: string | undefined = findPropertyValue(selfOnRB?.properties, MEDISPRING_INDENTIFIER)

          if (!existingMedispringDocumentId) {
            const newMedispringDocumentId = uuid()
            const newMedispringDoc = await documentApi.createDocument(
              await documentApi.withEncryptionMetadataUnlinked(
                new DecryptedDocument({
                  id: newMedispringDocumentId,
                }),
              ),
            )

            if (!newMedispringDoc) {
              throw new Error('Failed to create user MS document')
            }
            await documentApi.encryptAndSetMainAttachment(
              newMedispringDoc,
              ['public.json'],
              new Int8Array(
                new TextEncoder().encode(
                  JSON.stringify({
                    username,
                    password,
                    dataOwner: selfOrParentOnRB.id,
                  }),
                ),
              ),
            )

            const updatedSelfRB = new HealthcareParty({
              ...selfOnRB,
              properties: withUpsertedProperty(selfOnRB?.properties, MEDISPRING_INDENTIFIER, newMedispringDocumentId),
            })
            await healthcarePartyApi.modifyHealthcareParty(updatedSelfRB)
          } else {
            const existingMedispringDocument = await documentApi.getDocument(existingMedispringDocumentId).catch(() => null)

            if (!existingMedispringDocument) {
              throw new Error('MedispringDocument document is missing')
            }

            await documentApi.encryptAndSetMainAttachment(
              existingMedispringDocument,
              ['public.json'],
              new Int8Array(
                new TextEncoder().encode(
                  JSON.stringify({
                    username,
                    password,
                    dataOwner: selfOrParentOnRB.id,
                  }),
                ),
              ),
            )

            const existingMedispringProperty = findProperty(selfOnRB?.properties, MEDISPRING_INDENTIFIER)
            if (isLegacyProperty(existingMedispringProperty)) {
              const migratedSelfRB = new HealthcareParty({
                ...selfOnRB,
                properties: withUpsertedProperty(selfOnRB?.properties, MEDISPRING_INDENTIFIER, existingMedispringDocumentId),
              })
              await healthcarePartyApi.modifyHealthcareParty(migratedSelfRB)
            }
          }

          const me = await userApi.getCurrentUser()
          await userApi.modifyUser(
            new User({
              ...me,
              autoDelegations: {
                ...me.autoDelegations,
                [DelegationTag.All]: (me.autoDelegations[DelegationTag.All as 'All'] ?? []).filter((x) => x !== selfOrParentOnRB.id).concat(selfOrParentOnRB.id),
              },
            }),
          )

          return true
        })
      },
    }),
    setPractitionerMonaApiKey: builder.mutation<
      HealthcareParty | undefined,
      {
        practitioner: HealthcareParty
        monaApiKey: string
      }
    >({
      async queryFn({ practitioner, monaApiKey }, { getState }) {
        const practitionerApi = (await cardinalApi(getState))?.healthcareParty
        const documentApi: DocumentApi | undefined = (await cardinalApi(getState))?.document

        return guard([practitionerApi, documentApi], async ([practitionerApi, documentApi]): Promise<HealthcareParty> => {
          const existingMonaDocumentId: string | undefined = findPropertyValue(practitioner.properties, MONA_KEY_INDENTIFIER)

          if (!monaApiKey) {
            throw new Error('Mona Key is missing')
          }
          if (!existingMonaDocumentId) {
            const newMonaDocumentId = uuid()
            const newMonaDocument = await documentApi.createDocument(
              await documentApi.withEncryptionMetadataUnlinked(
                new DecryptedDocument({
                  id: newMonaDocumentId,
                }),
              ),
            )
            if (!newMonaDocument) {
              throw new Error('Failed to create user Mona Key document')
            }
            await documentApi.encryptAndSetMainAttachment(
              newMonaDocument,
              ['public.json'],
              new Int8Array(
                new TextEncoder().encode(
                  JSON.stringify({
                    monaApiKey,
                    dataOwner: practitioner.id,
                  }),
                ),
              ),
            )

            const practitionerWithMona = new HealthcareParty({
              ...practitioner,
              properties: withUpsertedProperty(practitioner.properties, MONA_KEY_INDENTIFIER, newMonaDocumentId),
            })
            return await practitionerApi.modifyHealthcareParty(practitionerWithMona)
          } else {
            const existingMonaDocument = await documentApi.getDocument(existingMonaDocumentId).catch(() => null)

            if (!existingMonaDocument) {
              throw new Error('MonaApiKey document is missing')
            }

            await documentApi.encryptAndSetMainAttachment(
              existingMonaDocument,
              ['public.json'],
              new Int8Array(
                new TextEncoder().encode(
                  JSON.stringify({
                    monaApiKey,
                    dataOwner: practitioner.id,
                  }),
                ),
              ),
            )

            const existingMonaProperty = findProperty(practitioner.properties, MONA_KEY_INDENTIFIER)
            if (isLegacyProperty(existingMonaProperty)) {
              const migratedPractitioner = new HealthcareParty({
                ...practitioner,
                properties: withUpsertedProperty(practitioner.properties, MONA_KEY_INDENTIFIER, existingMonaDocumentId),
              })
              return await practitionerApi.modifyHealthcareParty(migratedPractitioner)
            }

            return practitioner
          }
        })
      },
      invalidatesTags: (result) =>
        !!result
          ? [
              { type: 'Practitioner', id: 'all' },
              { type: 'Practitioner', id: result.id },
            ]
          : [],
    }),
  }),
})

export const { useGiveAccessToNewHealthcarePartyWithPrivateKeyMutation, useSetPractitionerMonaApiKeyMutation } = interopApiRtk
